import { cn } from "../../lib/utils";
import React, { useEffect, useRef } from "react";
import { createNoise3D } from "simplex-noise";
 
export const WavyBackground = ({
  children,
  className,
  containerClassName,
  colors,
  waveWidth,
  backgroundFill,
  blur = 0,
  speed = "fast",
  waveOpacity = 0.5,
  ...props
}: {
  children?: any;
  className?: string;
  containerClassName?: string;
  colors?: string[];
  waveWidth?: number;
  backgroundFill?: string;
  blur?: number;
  speed?: "slow" | "fast";
  waveOpacity?: number;
  [key: string]: any;
}) => {
  const noise = createNoise3D();
  let w: number,
    h: number,
    nt: number,
    i: number,
    x: number,
    ctx: any,
    canvas: any;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const getSpeed = () => {
    switch (speed) {
      case "slow":
        return 0.001;
      case "fast":
        return 0.002;
      default:
        return 0.001;
    }
  };
 
  const init = () => {
    canvas = canvasRef.current;
    ctx = canvas.getContext("2d");
    w = ctx.canvas.width = window.innerWidth;
    h = ctx.canvas.height = window.innerHeight;
    ctx.filter = `blur(${blur}px)`;
    nt = 0;
    window.onresize = function () {
      w = ctx.canvas.width = window.innerWidth;
      h = ctx.canvas.height = window.innerHeight;
      ctx.filter = `blur(${blur}px)`;
    };
    render();
  };
 
  const waveColors = colors ?? [
    "rgba(56, 189, 248, 0.3)",  // Light blue with transparency
    "rgba(129, 140, 248, 0.3)", // Light purple with transparency
    "rgba(192, 132, 252, 0.3)", // Light violet with transparency
    "rgba(232, 121, 249, 0.3)", // Light pink with transparency
    "rgba(34, 211, 238, 0.3)",  // Light cyan with transparency
  ];
  const drawWave = (n: number) => {
    nt += getSpeed();
    for (i = 0; i < n; i++) {
      ctx.beginPath();
      ctx.lineWidth = waveWidth || 50;
      ctx.strokeStyle = waveColors[i % waveColors.length];
      for (x = 0; x < w; x += 5) {
        var y = noise(x / 800, 0.05 * i, nt) * 400;
        ctx.lineTo(x, y + h * 0.5); // adjust for height, currently at 50% of the container
      }
      ctx.stroke();
      ctx.closePath();
    }
  };
 
  let animationId: number;
  const render = () => {
    ctx.fillStyle = backgroundFill || "black";
    ctx.globalAlpha = waveOpacity || 0.2;
    ctx.fillRect(0, 0, w, h);
    drawWave(10);
    animationId = requestAnimationFrame(render);
  };
 
  useEffect(() => {
    init();
    return () => {
      cancelAnimationFrame(animationId);
    };
    // Mount-only: init() starts its own animation loop, so re-running it would stack loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
 
  return (
    <div className={cn("relative flex flex-col items-center justify-center", containerClassName)}>
        <canvas
            className="absolute inset-0 z-0 w-full h-full blur-md"
            ref={canvasRef}
            id="canvas"
        ></canvas>
        <div className={cn("relative z-10", className)} {...props}>
            {children}
        </div>
    </div>
  );
};